/**
 * Measures (docs/17-getraenke.md, Mengen in ml, Maße mit Namen): stored and computed in ml, shown
 * by a name. Nobody orders 125 ml of Barolo, they order „1/8“. A name is looked up in four levels,
 * the more special one wins: the workspace's own names (Schoppen), the kind within the profile
 * (Schaumwein 0,1 l), the profile (Wein 1/8 l, Magnum), the core (4 cl, 0,3 l, 1,5 l).
 */

/** Names for serving sizes (glass) and container sizes (size), by ml. */
export interface MeasureNames {
  glass?: Readonly<Record<number, string>>;
  size?: Readonly<Record<number, string>>;
}

/** What a profile names: its own measures and those of a kind („Schaumwein“) within it. */
export interface ProfileMeasures extends MeasureNames {
  kinds?: Readonly<Record<string, MeasureNames>>;
}

/** A workspace's own name for a measure: „Schoppen“ for 500 ml. Always shown with the volume. */
export interface CustomMeasure {
  ml: number;
  name: string;
}

export type MeasureRole = "glass" | "size";

export interface MeasureContext {
  profile?: ProfileMeasures;
  kind?: string;
  custom?: readonly CustomMeasure[];
  /** The workspace writes glass sizes as decimals (B5): „0,25 l“, not „1/4 l“; names such as „Halbe“ stay. */
  decimal?: boolean;
}

/** A name that is only a fraction of a litre: „1/8 l“, „1/4 l“. */
const FRACTION = /^\d+\/\d+ l$/;

const comma = (n: number) => String(n).replace(".", ",");

/** The volume as written on a label: „0,75 l“, „1,5 l“, „3 l“. */
export const litres = (ml: number) => `${comma(Math.round(ml) / 1000)} l`;

/** The core's name, the last level: below 100 ml in cl („4 cl“), else in litres („0,3 l“, „1,5 l“). */
export const volumeText = (ml: number) => (ml < 100 ? `${comma(Math.round(ml) / 10)} cl` : litres(ml));

/** A name that is a word („Magnum“, „Schoppen“) gets the volume next to it; the menu must state it. */
const withVolume = (name: string, ml: number) => (/\d/.test(name) ? name : `${name} ${volumeText(ml)}`);

function lookup(ml: number, role: MeasureRole, ctx: MeasureContext): string | undefined {
  const own = ctx.custom?.find((c) => c.ml === ml)?.name;
  if (own) return own;
  const kind = ctx.kind ? ctx.profile?.kinds?.[ctx.kind]?.[role]?.[ml] : undefined;
  const name = kind ?? ctx.profile?.[role]?.[ml];
  return ctx.decimal && name && FRACTION.test(name) ? undefined : name;
}

/** How a measure is shown: „1/8 l“, „0,1 l“ (Sekt), „2 cl“, „Magnum 1,5 l“, „Schoppen 0,5 l“. */
export function measureName(ml: number, role: MeasureRole, ctx: MeasureContext = {}): string {
  const name = lookup(ml, role, ctx);
  return name ? withVolume(name, ml) : volumeText(ml);
}

/** All names a context knows, lower case, for reading them back. */
function names(ctx: MeasureContext): Map<string, number> {
  const out = new Map<string, number>();
  const add = (table: Readonly<Record<number, string>> | undefined) => {
    for (const [ml, name] of Object.entries(table ?? {})) if (!/\d/.test(name)) out.set(name.toLowerCase(), Number(ml));
  };
  add(ctx.profile?.glass);
  add(ctx.profile?.size);
  if (ctx.kind) {
    add(ctx.profile?.kinds?.[ctx.kind]?.glass);
    add(ctx.profile?.kinds?.[ctx.kind]?.size);
  }
  for (const c of ctx.custom ?? []) out.set(c.name.toLowerCase(), c.ml);
  return out;
}

/**
 * Reads a measure in any form: „1/8“, „0,125“, „125 ml“, „12,5 cl“, „0,75 l“, „Magnum“, „Schoppen“
 * (also „Schoppen 0,5 l“). A number without unit up to 60 is litres (0,75; 1,5; a keg of 30),
 * above that ml (330, 750). Null when it is none.
 */
export function parseMeasure(text: string, ctx: MeasureContext = {}): number | null {
  const t = text.trim().toLowerCase().replace(/\s+/g, " ");
  if (t === "") return null;
  const known = names(ctx);
  const word = /^([^\d(]+?)\s*(?:\(?\s*[\d.,/]+\s*(?:ml|cl|l)?\s*\)?)?$/.exec(t);
  if (word && known.has(word[1]!.trim())) return known.get(word[1]!.trim())!;
  const m = /^(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s*(ml|cl|l|liter)?$/.exec(t);
  if (!m) return null;
  let n: number;
  if (m[1]!.includes("/")) {
    const [a, b] = m[1]!.split("/").map((s) => Number(s.trim()));
    if (!b) return null;
    n = a! / b;
  } else n = Number(m[1]!.replace(",", "."));
  const unit = m[2] ?? (m[1]!.includes("/") || n <= 60 ? "l" : "ml");
  const ml = Math.round(unit === "ml" ? n : unit === "cl" ? n * 10 : n * 1000);
  return ml > 0 ? ml : null;
}

/** The workspace's own names as typed, one per line: „Schoppen = 0,5 l“, „Stange 0,2“. */
export function parseCustomMeasures(text: string): { value: CustomMeasure[] } | { error: string } {
  const out: CustomMeasure[] = [];
  for (const line of text.split(/[\n;]+/).map((l) => l.trim()).filter(Boolean)) {
    const m = /^([^\d=:]+?)\s*[=:]?\s*(\d.*)$/.exec(line);
    const ml = m ? parseMeasure(m[2]!) : null;
    if (!m || ml === null) return { error: `„${line}“: bitte Name und Menge, z. B. „Schoppen = 0,5 l“` };
    out.push({ name: m[1]!.trim(), ml });
  }
  return { value: out.sort((a, b) => a.ml - b.ml) };
}

/** The own names as they are typed and shown: „Schoppen = 0,5 l“, one per line. */
export const formatCustomMeasures = (list: readonly CustomMeasure[]) => list.map((c) => `${c.name} = ${volumeText(c.ml)}`).join("\n");
